// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  DownloadMyVcardData,
  DownloadMyVcardResponses,
  DownloadVcardData,
  DownloadVcardErrors,
  DownloadVcardResponses,
  GetCardData,
  GetCardErrors,
  GetCardPhotoData,
  GetCardPhotoErrors,
  GetCardPhotoResponses,
  GetCardResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeResponses,
  GetMyCardData,
  GetMyCardPhotoData,
  GetMyCardPhotoResponses,
  GetMyCardResponses,
  LogoutData,
  LogoutResponses,
  ResolveCardsData,
  ResolveCardsErrors,
  ResolveCardsResponses,
  SearchCardsData,
  SearchCardsErrors,
  SearchCardsResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Get My Card
 *
 * The signed-in employee's business card, built from their Entra profile.
 */
export const getMyCard = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyCardData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMyCardResponses, unknown, ThrowOnError>({
    url: "/card/me",
    ...options,
  });

/**
 * Get My Card Photo
 *
 * The signed-in employee's profile photo (JPEG), 404 when none is set.
 */
export const getMyCardPhoto = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyCardPhotoData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMyCardPhotoResponses, unknown, ThrowOnError>({
    url: "/card/me/photo",
    ...options,
  });

/**
 * Download My Vcard
 *
 * Download the signed-in employee's card as a .vcf file.
 */
export const downloadMyVcard = <ThrowOnError extends boolean = false>(
  options?: Options<DownloadMyVcardData, ThrowOnError>,
) =>
  (options?.client ?? client).get<DownloadMyVcardResponses, unknown, ThrowOnError>({
    url: "/card/me/vcard",
    ...options,
  });

/**
 * Search Cards
 *
 * Colleague lookup: enabled member accounts matching a name or mail prefix.
 */
export const searchCards = <ThrowOnError extends boolean = false>(
  options: Options<SearchCardsData, ThrowOnError>,
) =>
  (options.client ?? client).get<SearchCardsResponses, SearchCardsErrors, ThrowOnError>({
    url: "/card/search",
    ...options,
  });

/**
 * Resolve Cards
 *
 * The cards of a print export, one e-mail address per colleague.
 *
 * Addresses only, never names: an address identifies exactly one person, so
 * the export never guesses. An address without a card holder comes back in
 * `not_found`; a colleague listed twice is returned once.
 */
export const resolveCards = <ThrowOnError extends boolean = false>(
  options: Options<ResolveCardsData, ThrowOnError>,
) =>
  (options.client ?? client).post<ResolveCardsResponses, ResolveCardsErrors, ThrowOnError>({
    url: "/card/batch",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Get Card
 *
 * A colleague's business card by Entra object id.
 */
export const getCard = <ThrowOnError extends boolean = false>(
  options: Options<GetCardData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetCardResponses, GetCardErrors, ThrowOnError>({
    url: "/card/{user_id}",
    ...options,
  });

/**
 * Get Card Photo
 *
 * A colleague's profile photo (JPEG), 404 when none is set.
 */
export const getCardPhoto = <ThrowOnError extends boolean = false>(
  options: Options<GetCardPhotoData, ThrowOnError>,
) =>
  (options.client ?? client).get<GetCardPhotoResponses, GetCardPhotoErrors, ThrowOnError>({
    url: "/card/{user_id}/photo",
    ...options,
  });

/**
 * Download Vcard
 *
 * Download a colleague's card as a .vcf file.
 */
export const downloadVcard = <ThrowOnError extends boolean = false>(
  options: Options<DownloadVcardData, ThrowOnError>,
) =>
  (options.client ?? client).get<DownloadVcardResponses, DownloadVcardErrors, ThrowOnError>({
    url: "/card/{user_id}/vcard",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie and drop any delegated Graph token.
 *
 * The session is read best-effort rather than through `SessionDep`: signing
 * out with an already-expired cookie has to keep working, and a 401 here
 * would leave the browser holding it. Dropping the Graph token is local only
 * — the consent still stands at Entra — but it stops a live delegated token,
 * and the refresh token behind it when `offline_access` was approved, from
 * outliving the session that obtained it and being inherited by the next
 * one signed in under the same email.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Return the current session's identity.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });
